import clsx from "clsx";
import Image from "next/image";
import { useState } from "react";
import ChevronDown from "@/components/icons/ChevronDown";
import {
  AccordionContent,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import accordionStyles from "@/components/ui/Accordion/index.module.css";
import Button from "@/components/ui/Button";
import { useTranslations } from "@/i18n/client";
import { formatDate } from "@/lib/utils/dateUtils";
import type { IOrder } from "../order";
import { type OrderPaging, useOrders } from "../useOrders";
import styles from "./index.module.css";

const OrdersAccordions = () => {
  const t = useTranslations();

  return (
    <div className={styles.tableContainer}>
      <p className={styles.tableMobileTitle}>
        {t("myAccount.accountPage.tabs.tableHeader.orders")}
      </p>

      <div className={clsx(styles.tableDesktopTitle, styles.tableGrid)}>
        <p className={styles.orderRefTitle}>
          {t("myAccount.accountPage.tabs.tableHeader.orderRef")}
        </p>
        <p className={styles.orderDateTitle}>
          {t("myAccount.accountPage.tabs.tableHeader.orderDate")}
        </p>
        <p className={styles.orderTotalTitle}>
          {t("myAccount.accountPage.tabs.tableHeader.orderTotal")}
        </p>
      </div>

      <OrdersAccordionLoader />
    </div>
  );
};
export default OrdersAccordions;

interface IOrderList {
  orders: IOrder[];
}

const OrdersAccordionLoader = ({ from = 0, size = 50 }: OrderPaging) => {
  const [showNextPage, setShowNextPage] = useState(false);
  const { data, isLoading } = useOrders({ from, size });
  const t = useTranslations();

  if (isLoading)
    return (
      <div className={styles.loadMoreContainer}>
        <h3>{t("common.loading")}</h3>
      </div>
    );

  const orders = data?.orders;

  const total = data?.ordersPaging.totalSize;
  const hasMore = (orders?.length ?? 0) < (total ?? 0);

  if (!orders?.length && from === 0)
    return <h3>{t("myAccount.accountPage.orders.empty")}</h3>;

  return (
    <>
      {orders && <OrdersListAccordions orders={orders} />}
      {hasMore && !showNextPage && (
        <div className={styles.loadMoreContainer}>
          <Button
            onClick={() => setShowNextPage(true)}
            className={styles.loadMore}
          >
            {t("common.loadMore")}
          </Button>
        </div>
      )}
      {showNextPage && <OrdersAccordionLoader from={from + size} size={size} />}
    </>
  );
};

export function OrdersListAccordions({ orders }: IOrderList) {
  return (
    <>
      {orders.map(order => {
        return (
          <div key={order.order}>
            <AccordionRoot>
              <AccordionItem className={styles.tableRowBorder}>
                <AccordionTrigger icon={null} className={styles.tableSummary}>
                  <div className={clsx(styles.tableRow, styles.tableGrid)}>
                    <p className={styles.orderId}>#{order.order}</p>
                    <time
                      className={styles.orderDate}
                      dateTime={formatDate(order.date)}
                    >
                      {formatDate(order.date)}
                    </time>
                    <p className={styles.orderTotal}>
                      {order.totals.itemsTotalPrice}
                      <span className={clsx(accordionStyles.icon, styles.icon)}>
                        <ChevronDown />
                      </span>
                    </p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className={styles.accordionElements}>
                  {order.items.map(item => {
                    return (
                      <div
                        className={styles.productInfoContainer}
                        key={item.line}
                      >
                        <div className={styles.productInfo}>
                          <div className={styles.productImage}>
                            <Image
                              width={78}
                              height={100}
                              src={`${item.product?.media.full?.[1]}` || ""}
                              alt={`Picture of ${item.product?.variantName || "unknown product"}`}
                            />
                            <div className={styles.productDescription}>
                              {item.product && <p>{item.product.name}</p>}
                              <p className={styles.sizeText}>
                                {item.size}{" "}
                                {item.product && (
                                  <span>/ {item.product.variantName}</span>
                                )}
                              </p>
                            </div>
                          </div>

                          <p>{item.totalPrice}</p>
                        </div>
                        <div></div>
                      </div>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            </AccordionRoot>
          </div>
        );
      })}
    </>
  );
}
